import { DocumentError } from 'apiConnector/document/document';
import { ErrorDisplayContext, mapToLegacyError } from 'components/shared/Document/MultiLanguageDocumentItem';
import { IconWithPopover } from 'components/shared/ErrorsPanel/Details/Icon/IconWithPopover';
import {
  ErrorDetails,
  ErrorDetailsWithoutSummary,
  ErrorDetailsWithSummary,
  ErrorMessagesContext,
} from 'contexts/errorMessages/ErrorMessages.context';
import { useTranslation } from 'hooks/useTranslation';
import { IntlKey } from 'locales';
import { DocumentType } from 'models/documents/DocumentType';
import { type FC, type PropsWithChildren, useMemo } from 'react';
import { getSummaryFromErrorMessage } from 'utils/errors/workflowErrors';

export type ErrorIconProps = {
  e2e: string;
  locale?: string;
  error: DocumentError;
  errorContext: ErrorDisplayContext;
  documentType: DocumentType;
  isInitiallyOpened?: boolean;
};

function getErrorMessageKey(docType: DocumentType): IntlKey {
  switch (docType) {
    case 'Brochure':
      return 'downloadBrochure.error';
    case 'PreConfirmation':
      return 'downloadPreconfirmation.error';
    case 'TermSheet':
      return 'downloadTermsheet.error';
    default:
      return 'downloadDocument.error';
  }
}

function getFunctionalErrorMessageKey(docType: DocumentType): IntlKey {
  switch (docType) {
    case 'Brochure':
      return 'downloadBrochure.functional.error';
    case 'TermSheet':
      return 'downloadTermsheet.functional.error';
    default:
      return 'downloadDocument.functional.error';
  }
}

export const ErrorIcon: FC<PropsWithChildren<ErrorIconProps>> = ({
  e2e,
  locale,
  error,
  documentType,
  errorContext: { shouldDisplayErrorDetails, shouldUseRejectionCode },
  isInitiallyOpened,
}) => {
  const downloadTermsheetErrorMessage = useTranslation(getErrorMessageKey(documentType));
  const downloadTermsheetErrorMessageForExternal = useTranslation(getFunctionalErrorMessageKey(documentType));

  const rejections = error.type === 'REJECTION' ? error.rejections : null;

  const context: ErrorDetails = useMemo(() => {
    if (error.type === 'REJECTION') {
      if (shouldDisplayErrorDetails) {
        return {
          errorMessages: rejections,
          hasSummaryFromErrorMessages: false,
          shouldUseRejectionCode,
        } as ErrorDetailsWithoutSummary;
      } else {
        const summaryFromErrorMessages = [
          ...new Set(
            rejections
              ?.map(mapToLegacyError)
              ?.map(errorMessages => getSummaryFromErrorMessage(errorMessages, shouldUseRejectionCode)),
          ),
        ];

        const formattedSummaryMessages = summaryFromErrorMessages.map(msg => msg.split('\r\n'));

        return {
          technicalError: downloadTermsheetErrorMessageForExternal,
          summaryFromErrorMessages,
          formattedSummaryMessages,
          hasSummaryFromErrorMessages: true,
          shouldUseRejectionCode,
        } as ErrorDetailsWithSummary;
      }
    } else if (error.type === 'END_USER_ERROR') {
      return {
        errorMessages: error.errorMessages.map(m => ({ RejectionMessage: m })),
        hasSummaryFromErrorMessages: false,
        shouldUseRejectionCode: false,
      } as ErrorDetailsWithoutSummary;
    } else {
      return {
        technicalError: downloadTermsheetErrorMessage,
        hasSummaryFromErrorMessages: false,
        shouldUseRejectionCode,
      } as ErrorDetailsWithoutSummary;
    }
  }, [
    downloadTermsheetErrorMessage,
    downloadTermsheetErrorMessageForExternal,
    rejections,
    error,
    shouldDisplayErrorDetails,
    shouldUseRejectionCode,
  ]);

  return (
    <ErrorMessagesContext.Provider value={context}>
      <IconWithPopover
        id="downloadTermsheet-error-icons"
        isInitiallyOpened={isInitiallyOpened}
        e2e={`${e2e}-error-icon-${locale}`}
        placement="top"
        className="pt-0"
      />
    </ErrorMessagesContext.Provider>
  );
};
